// iter-386u-p / iter-386u-q — Vendor Project Ledger with 3-tab layout that
// mirrors the admin ProjectAccounts UX (Overview / Transactions / Ledger).
// A vendor can also raise a new RA Bill from the Transactions tab.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Briefcase, Receipt, TrendingUp, Plus, FileText, Eye } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { API } from '@/App';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function StatusPill({ status, palette = {} }) {
  const s = (status || 'draft').toLowerCase();
  const map = {
    approved: 'bg-green-100 text-green-700',
    submitted: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    active: 'bg-purple-100 text-purple-700',
    paid: 'bg-red-100 text-red-700',
    received: 'bg-green-100 text-green-700',
    draft: 'bg-stone-100 text-stone-600',
    ...palette,
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${map[s] || map.draft}`}>{s}</span>;
}

// ─── Overview Tab ───────────────────────────────────────────────────────────
function VendorOverviewTab({ ledger }) {
  const s = ledger?.summary || {};
  return (
    <div className="p-6 space-y-6" data-testid="vendor-overview-tab">
      <div className="apple-card p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-stone-900">{ledger?.project?.name}</h2>
            <p className="text-sm text-stone-500 mt-1">Vendor-scoped account summary</p>
          </div>
          <div className="text-xs text-stone-400 uppercase tracking-wide">In Progress</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-6">
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-xs text-stone-500">Total Approved</p>
            <p className="text-xl font-bold text-blue-700 mt-1" data-testid="ov-total-approved">{money(s.total_approved)}</p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
            <p className="text-xs text-stone-500">Total Billed</p>
            <p className="text-xl font-bold text-purple-700 mt-1" data-testid="ov-total-billed">{money(s.total_billed)}</p>
          </div>
          <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
            <p className="text-xs text-stone-500">Total Received</p>
            <p className="text-xl font-bold text-green-700 mt-1" data-testid="ov-total-received">{money(s.total_received)}</p>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
            <p className="text-xs text-stone-500">Balance Pending</p>
            <p className="text-xl font-bold text-amber-700 mt-1" data-testid="ov-balance-pending">{money(s.balance_pending)}</p>
          </div>
        </div>
      </div>

      {/* Quick counts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="apple-card p-5">
          <div className="flex items-center gap-2 text-stone-600 mb-2">
            <FileText size={16} /> Submissions
          </div>
          <p className="text-2xl font-bold text-stone-900">{ledger?.submissions?.length || 0}</p>
          <p className="text-xs text-stone-500 mt-1">
            {(ledger?.submissions || []).filter(s => s.status === 'approved').length} approved
          </p>
        </div>
        <div className="apple-card p-5">
          <div className="flex items-center gap-2 text-stone-600 mb-2">
            <Receipt size={16} /> RA Bills
          </div>
          <p className="text-2xl font-bold text-stone-900">{ledger?.bills?.length || 0}</p>
          <p className="text-xs text-stone-500 mt-1">Running Bills generated</p>
        </div>
        <div className="apple-card p-5">
          <div className="flex items-center gap-2 text-stone-600 mb-2">
            <TrendingUp size={16} /> Payments
          </div>
          <p className="text-2xl font-bold text-stone-900">{ledger?.payments?.length || 0}</p>
          <p className="text-xs text-stone-500 mt-1">Payments received</p>
        </div>
      </div>
    </div>
  );
}

// ─── Transactions Tab ───────────────────────────────────────────────────────
function VendorTransactionsTab({ ledger, onCreateBill, onCreateCredit, onRefresh }) {
  const [type, setType] = useState('all');
  // Build a unified transaction list (bills as +ve billed, payments as -ve paid).
  const rows = [];
  (ledger?.bills || []).forEach((b) => rows.push({
    id: b.id || b.token,
    kind: 'bill',
    date: b.created_at,
    party: 'You (Vendor)',
    description: b.title || 'Running Bill',
    reference: b.submission_number || (b.token || '').slice(0, 8),
    method: '—',
    amount: b.total_amount,
    status: b.status || 'active',
  }));
  (ledger?.payments || []).forEach((p) => rows.push({
    id: p.id,
    kind: 'payment',
    date: p.created_at || p.payment_date,
    party: p.paid_by_name || 'Company',
    description: p.notes || 'Payment received',
    reference: p.reference || p.utr || '—',
    method: p.method || '—',
    amount: p.amount,
    status: 'received',
  }));
  (ledger?.credit_notes || []).forEach((c) => rows.push({
    id: c.id,
    kind: 'credit_note',
    date: c.date || c.created_at,
    party: 'You (Vendor)',
    description: c.reason || 'Credit Note',
    reference: (c.id || '').slice(0, 8),
    method: 'Credit Note',
    amount: c.amount,
    status: 'applied',
  }));
  const filtered = type === 'all' ? rows : rows.filter(r => r.kind === type);
  filtered.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const totals = {
    billed: rows.filter(r => r.kind === 'bill').reduce((s, r) => s + (r.amount || 0), 0),
    received: rows.filter(r => r.kind === 'payment').reduce((s, r) => s + (r.amount || 0), 0),
    credit: rows.filter(r => r.kind === 'credit_note').reduce((s, r) => s + (r.amount || 0), 0),
  };

  return (
    <div className="p-6 space-y-4" data-testid="vendor-transactions-tab">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-stone-800">All Transactions</h3>
          <p className="text-xs text-stone-500 mt-1">
            {filtered.length} transactions | Billed: {money(totals.billed)} | Received: {money(totals.received)} | Credit Notes: {money(totals.credit)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 border border-stone-300 rounded-md px-2 text-sm"
            data-testid="txn-type-filter"
          >
            <option value="all">All</option>
            <option value="bill">Bills only</option>
            <option value="payment">Payments only</option>
            <option value="credit_note">Credit Notes only</option>
          </select>
          <Button
            onClick={onCreateCredit}
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-50"
            data-testid="new-credit-note-btn"
          >
            <Plus size={14} className="mr-1" /> Create Credit Note
          </Button>
          <Button
            onClick={onCreateBill}
            className="bg-stone-900 hover:bg-stone-800 text-white"
            data-testid="new-transaction-btn"
          >
            <Plus size={14} className="mr-1" /> New Transaction
          </Button>
        </div>
      </div>

      <div className="apple-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500 border-b border-stone-200">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Party</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Mode</th>
              <th className="p-3 text-left">Notes</th>
              <th className="p-3 text-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const typeColor = r.kind === 'payment' ? 'bg-green-100 text-green-700'
                : r.kind === 'credit_note' ? 'bg-amber-100 text-amber-700'
                : 'bg-purple-100 text-purple-700';
              const label = r.kind === 'payment' ? 'Received' : r.kind === 'credit_note' ? 'Credit Note' : 'Billed';
              return (
                <tr key={`${r.kind}-${r.id}`} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`txn-row-${r.id}`}>
                  <td className="p-3 text-stone-600">{r.date ? new Date(r.date).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="p-3 text-stone-800">{r.party}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${typeColor}`}>{label}</span></td>
                  <td className="p-3 text-stone-600">{r.method}</td>
                  <td className="p-3 text-stone-600 text-xs">{r.description}</td>
                  <td className={`p-3 text-right font-semibold ${r.kind === 'payment' ? 'text-green-700' : r.kind === 'credit_note' ? 'text-amber-700' : 'text-purple-700'}`}>
                    {r.kind !== 'bill' ? '+' : ''}{money(r.amount)}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-stone-400 italic">No transactions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Ledger / Statement Tab ─────────────────────────────────────────────────
function VendorStatementTab({ ledger, onDownload, downloading, onDownloadTally, downloadingTally }) {
  const s = ledger?.summary || {};
  // iter-386u-x — canonical formula: Total Billed − Total Received − Credit Notes.
  // A Debit (bill) increases the closing balance; a Credit (payment / credit note)
  // decreases it.
  const closing = (s.total_billed || 0) - (s.total_received || 0) - (s.total_credit_notes || 0);
  const opening = 0;
  const [sortDir, setSortDir] = useState('newest'); // iter-386u-t: Newest / Oldest toggle

  // Combined ledger rows (chronological)
  const rows = [];
  (ledger?.bills || []).forEach((b) => rows.push({
    id: b.id || b.token, date: b.created_at, party: 'You (Vendor)',
    desc: `Running Bill #${b.submission_number || (b.token || '').slice(0, 8)}`,
    mode: '—', debit: b.total_amount, credit: 0,
    _bill: b,   // iter-386u-v — keep source ref so View Details link works
  }));
  (ledger?.payments || []).forEach((p) => rows.push({
    id: p.id, date: p.created_at || p.payment_date, party: p.paid_by_name || 'Company',
    desc: p.notes || 'Payment received', mode: p.method || '—',
    debit: 0, credit: p.amount,
  }));
  (ledger?.credit_notes || []).forEach((c) => rows.push({
    id: c.id, date: c.date || c.created_at, party: 'You (Vendor)',
    desc: `Credit Note — ${c.reason || 'Adjustment'}`,
    mode: 'Credit Note', debit: 0, credit: c.amount,
  }));
  // Always compute running balance oldest→newest, then apply display sort.
  // iter-386u-x — canonical direction: Debit (bill) adds, Credit (payment / CN) subtracts.
  rows.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  let running = 0;
  const withBalance = rows.map(r => { running += ((r.debit || 0) - (r.credit || 0)); return { ...r, balance: running }; });
  const rendered = sortDir === 'newest' ? [...withBalance].reverse() : withBalance;

  return (
    <div className="p-6 space-y-4" data-testid="vendor-statement-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-stone-800">{ledger?.project?.name} — Account Summary</h3>
          <p className="text-xs text-stone-500 mt-1">Vendor-scoped ledger statement</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            className="h-9 border border-stone-300 rounded-md px-2 text-sm"
            data-testid="statement-sort-dir"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <Button
            onClick={onDownloadTally}
            disabled={downloadingTally}
            variant="outline"
            className="border-stone-300"
            data-testid="download-ledger-tally-btn"
          >
            <Download size={14} className="mr-2" /> {downloadingTally ? 'Preparing…' : 'Download in Tally Format'}
          </Button>
          <Button
            onClick={onDownload}
            disabled={downloading}
            variant="outline"
            className="border-stone-300"
            data-testid="download-ledger-pdf-btn"
          >
            <Download size={14} className="mr-2" /> {downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        </div>
      </div>

      <div className="p-4 !bg-stone-900 text-white grid grid-cols-2 md:grid-cols-5 gap-4 rounded-lg border border-stone-800" data-testid="vendor-account-summary-card">
        <div><p className="text-xs uppercase text-stone-300">Opening Balance</p><p className="text-lg font-bold text-white">{money(opening)}</p></div>
        <div><p className="text-xs uppercase text-stone-300">Total Billed (Debit)</p><p className="text-lg font-bold text-purple-200">{money(s.total_billed)}</p></div>
        <div><p className="text-xs uppercase text-stone-300">Total Received (Credit)</p><p className="text-lg font-bold text-green-200">{money(s.total_received)}</p></div>
        <div><p className="text-xs uppercase text-stone-300">Credit Notes</p><p className="text-lg font-bold text-amber-200">{money(s.total_credit_notes)}</p></div>
        <div><p className="text-xs uppercase text-stone-300">Closing Balance</p><p className="text-lg font-bold text-white">{money(closing)}</p></div>
      </div>

      <div className="apple-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500 border-b border-stone-200">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Party</th>
              <th className="p-3 text-left">Description</th>
              <th className="p-3 text-left">Mode</th>
              <th className="p-3 text-right">Debit (₹)</th>
              <th className="p-3 text-right">Credit (₹)</th>
              <th className="p-3 text-right">Balance (₹)</th>
              <th className="p-3 text-right w-10">·</th>
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={8} className="p-3 text-stone-500 italic">Opening Balance</td></tr>
            {rendered.map(r => (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="p-3 text-stone-600">{r.date ? new Date(r.date).toLocaleDateString('en-IN') : '—'}</td>
                <td className="p-3 text-stone-800">{r.party}</td>
                <td className="p-3 text-stone-600">{r.desc}</td>
                <td className="p-3 text-stone-600">{r.mode}</td>
                <td className="p-3 text-right text-red-600">{r.debit ? money(r.debit) : '—'}</td>
                <td className="p-3 text-right text-green-700">{r.credit ? money(r.credit) : '—'}</td>
                <td className={`p-3 text-right font-semibold ${r.balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(r.balance)}</td>
                <td className="p-3 text-right">
                  {r._bill && (
                    <a
                      href={r._bill.source === 'admin_running_bill'
                        ? `/running-bills/${r._bill.id}`
                        : `/p/vendor-submit/${r._bill.token || r._bill.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-7 h-7 rounded text-stone-500 hover:bg-stone-100 hover:text-stone-800"
                      title="View bill details"
                      data-testid={`view-bill-${r._bill.id}`}
                    >
                      <Eye size={14} />
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {rendered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-stone-400 italic">No entries yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── New Transaction Dialog (Raise RA Bill) ─────────────────────────────────
function RaiseBillDialog({ open, onClose, submissions, onSuccess }) {
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const approved = (submissions || []).filter(s => s.status === 'approved' && (s.submission_type === 'quote' || s.submission_type === 'estimate'));

  useEffect(() => { if (open) setSelectedId(''); }, [open]);

  const submit = async () => {
    if (!selectedId) { toast.error('Select an approved quote'); return; }
    setBusy(true);
    try {
      // iter-386u-x — send our origin so the backend builds the public URL
      // against the current preview / production domain instead of falling
      // back to a hard-coded one (which produced a 403 on redirect).
      const r = await axios.post(`${API}/vendor-submissions/${selectedId}/convert-to-bill`, {
        base_url: window.location.origin,
      });
      // Prefer explicit public_url; otherwise stitch origin + returned public_path.
      const path = r?.data?.public_path;
      const url = r?.data?.public_url || (path ? `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}` : null);
      toast.success('Running Bill created');
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise RA bill');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="raise-bill-dialog">
        <DialogHeader>
          <DialogTitle>Raise New RA Bill</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600">
          Pick one of your approved quotes to bill against. A public RA Bill link will open for you to fill in the site quantities and submit.
        </p>
        {approved.length === 0 ? (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            No approved quotes / estimates available on this project. Submit one and wait for admin approval first.
          </div>
        ) : (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="h-10 w-full border border-stone-300 rounded-md px-2 text-sm"
            data-testid="raise-bill-select"
          >
            <option value="">Choose approved quote…</option>
            {approved.map(a => (
              <option key={a.id} value={a.id}>
                {a.submission_number || a.id.slice(0, 8)} — {a.title} — ₹{Number(a.total_amount || a.grand_total || 0).toLocaleString('en-IN')}
              </option>
            ))}
          </select>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!selectedId || busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="raise-bill-submit-btn"
          >
            {busy ? 'Creating…' : 'Add raised RA bill client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Credit Note Dialog ──────────────────────────────────────────────
function CreditNoteDialog({ open, onClose, projectId, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setAmount(''); setReason(''); } }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/vendor/projects/${projectId}/credit-notes`, {
        amount: amt,
        reason: reason.trim(),
      });
      toast.success('Credit note recorded — ledger balance updated');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create credit note');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle>Create Credit Note</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600">
          Record a discount or payment adjustment. This will offset your outstanding balance on this project.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Amount (₹)</label>
            <input
              type="number"
              value={amount}
              min="0"
              step="0.01"
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 h-10 w-full border border-stone-300 rounded-md px-3 text-sm"
              placeholder="e.g. 5000"
              data-testid="credit-note-amount"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-stone-300 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. Discount on delayed delivery"
              data-testid="credit-note-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="credit-note-submit-btn"
          >
            {busy ? 'Saving…' : 'Save Credit Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function VendorProjectLedger() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTally, setDownloadingTally] = useState(false);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'overview');
  const [billOpen, setBillOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/vendor/projects/${projectId}/ledger`);
      setLedger(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load ledger');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/vendor/projects/${projectId}/ledger/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor-ledger-${ledger?.project?.name || projectId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download PDF');
    } finally { setDownloading(false); }
  };

  const downloadTally = async () => {
    setDownloadingTally(true);
    try {
      const r = await axios.get(`${API}/vendor/projects/${projectId}/ledger/tally`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tally-ledger-${ledger?.project?.name || projectId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tally CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download Tally CSV');
    } finally { setDownloadingTally(false); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'statement', label: 'Ledger / Statement' },
  ];

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96 text-stone-400">Loading project ledger…</div></Layout>;
  }
  if (!ledger) return <Layout><p className="text-stone-500 p-6">No ledger data</p></Layout>;

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="vendor-project-ledger-page">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-stone-500 hover:text-stone-800 flex items-center gap-1 text-sm" data-testid="ledger-back-btn">
              <ArrowLeft size={16} /> Back
            </button>
            <span className="text-stone-300">/</span>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Project Ledger</p>
              <p className="font-semibold text-stone-900">{ledger.project?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase size={16} className="text-stone-400" />
            <span className="text-sm text-stone-600">Vendor view</span>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="bg-white border-b border-stone-200 px-6 flex items-center gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-stone-900 text-stone-900'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
              data-testid={`vendor-ledger-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto bg-stone-50">
          {tab === 'overview' && <VendorOverviewTab ledger={ledger} />}
          {tab === 'transactions' && (
            <VendorTransactionsTab
              ledger={ledger}
              onCreateBill={() => setBillOpen(true)}
              onCreateCredit={() => setCreditOpen(true)}
              onRefresh={load}
            />
          )}
          {tab === 'statement' && (
            <VendorStatementTab
              ledger={ledger}
              onDownload={downloadPdf}
              downloading={downloading}
              onDownloadTally={downloadTally}
              downloadingTally={downloadingTally}
            />
          )}
        </div>

        <RaiseBillDialog
          open={billOpen}
          onClose={() => setBillOpen(false)}
          submissions={ledger.submissions}
          onSuccess={load}
        />
        <CreditNoteDialog
          open={creditOpen}
          onClose={() => setCreditOpen(false)}
          projectId={projectId}
          onSuccess={load}
        />
      </div>
    </Layout>
  );
}
